import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import api from "../../lib/api";
import { CATEGORY_LABEL } from "../../lib/categories";

const fallback = [
    {
        id: "f1",
        title: "Notizbücher",
        subtitle: "Für Worte, die zählen",
        desc: "Cremefarbenes Papier, weiche Einbände — Begleiter für Gedanken, Träume und stille Momente.",
        img: "https://images.unsplash.com/photo-1726938757756-c3fb24fb49c1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MTN8MHwxfHNlYXJjaHwxfHxhZXN0aGV0aWMlMjBub3RlYm9vayUyMGpvdXJuYWwlMjBjcmVhbSUyMG5ldXRyYWx8ZW58MHx8fHwxNzc3Nzk1NzEyfDA&ixlib=rb-4.1.0&q=85",
    },
    {
        id: "f2",
        title: "Stifte",
        subtitle: "Sanfter Strich",
        desc: "Fein laufende Tinte, ausgewogene Form. Werkzeuge, die das Schreiben zu einem Ritual machen.",
        img: "https://images.unsplash.com/photo-1741720928040-7b48f76a874c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzOTB8MHwxfHNlYXJjaHwyfHxlbGVnYW50JTIwcGVuJTIwc3RhdGlvbmVyeSUyMGRlc2slMjBuZXV0cmFsfGVufDB8fHx8MTc3Nzc5NTcxMnww&ixlib=rb-4.1.0&q=85",
    },
    {
        id: "f3",
        title: "Geschenke",
        subtitle: "Worte, die bleiben",
        desc: "Sorgfältig kuratierte Sets, eingehüllt in warmes Papier — kleine Botschaften für besondere Menschen.",
        img: "https://images.unsplash.com/photo-1751450236048-aa1981f7bc2d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzOTB8MHwxfHRoZW91Z2h0ZnVsJTIwZ2lmdCUyMHdyYXBwZWQlMjBuZXV0cmFsJTIwYWVzdGhldGljfGVufDB8fHx8MTc3Nzc5NTcxMnww&ixlib=rb-4.1.0&q=85",
    },
];

export default function ComingSoon() {
    const [products, setProducts] = useState(null); // null=loading, []=empty, [...]=loaded

    useEffect(() => {
        let active = true;
        api.get("/products")
            .then((res) => {
                if (!active) return;
                const nonBooks = res.data.filter((p) => p.category !== "buch");
                setProducts(nonBooks.slice(0, 3));
            })
            .catch(() => active && setProducts([]));
        return () => {
            active = false;
        };
    }, []);

    const showLive = Array.isArray(products) && products.length > 0;
    const items = showLive ? products : fallback;

    return (
        <section
            id="produkte"
            data-testid="products-section"
            className="relative py-24 md:py-32 bg-cream-50"
        >
            <div className="max-w-6xl mx-auto px-6 md:px-10">
                <div className="text-center max-w-2xl mx-auto reveal">
                    <span className="divider-dot mb-8">
                        <span>{showLive ? "Aus dem Sortiment" : "Bald erhältlich"}</span>
                    </span>
                    <h2
                        className="font-serif font-light text-4xl sm:text-5xl text-ink-900 tracking-tight leading-[1.1]"
                        data-testid="products-heading"
                    >
                        {showLive ? (
                            <>
                                Was bei uns{" "}
                                <em className="italic text-mauve-500">
                                    erblüht
                                </em>
                            </>
                        ) : (
                            <>
                                Was bei uns{" "}
                                <em className="italic text-mauve-500">
                                    entsteht
                                </em>
                            </>
                        )}
                    </h2>
                    <p className="mt-6 text-base md:text-lg text-ink-700 leading-relaxed font-light">
                        {showLive
                            ? "Eine kleine Auswahl unserer aktuellen Lieblinge — sorgfältig kuratiert."
                            : "Drei kleine Welten, mit Bedacht gewählt — bald in deinen Händen."}
                    </p>
                </div>

                <div className="mt-20 grid md:grid-cols-3 gap-10 md:gap-8">
                    {items.map((p, idx) => {
                        const isLive = showLive;
                        const title = isLive ? p.name : p.title;
                        const subtitle = isLive
                            ? CATEGORY_LABEL[p.category]
                            : p.subtitle;
                        const desc = isLive ? p.description : p.desc;
                        const img = isLive ? p.image_url : p.img;
                        const badge = "Bald erhältlich";
                        return (
                            <article
                                key={p.id}
                                data-testid={`product-card-${p.id}`}
                                className={`reveal premium-card group p-3 ${
                                    idx === 1 ? "md:mt-12" : ""
                                }`}
                            >
                                <div className="aspect-[4/5] mb-6 relative overflow-hidden rounded-[1.35rem] bg-cream-100">
                                    <img
                                        src={img}
                                        alt={title}
                                        onError={(e) => {
                                            e.currentTarget.style.opacity =
                                                "0.4";
                                        }}
                                        className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.04]"
                                    />
                                    <div className="absolute top-4 left-4 rounded-full bg-cream-50/90 backdrop-blur-sm border border-white/70 px-3 py-1.5">
                                        <span className="text-[10px] tracking-[0.28em] uppercase text-ink-700 font-light">
                                            {badge}
                                        </span>
                                    </div>
                                </div>

                                <div className="px-2 pb-3">
                                    <p className="text-[11px] tracking-[0.28em] uppercase text-mauve-500 font-light mb-2">
                                        {subtitle}
                                    </p>
                                    <h3 className="font-serif text-3xl text-ink-900 mb-3">
                                        {title}
                                    </h3>
                                    <p className="text-sm text-ink-700 leading-relaxed font-light line-clamp-3">
                                        {desc}
                                    </p>
                                </div>
                            </article>
                        );
                    })}
                </div>

                <div className="mt-16 text-center reveal">
                    <Link
                        to="/produkte"
                        data-testid="products-section-cta"
                        className="group inline-flex items-center gap-3 border border-ink-900 text-ink-900 hover:bg-ink-900 hover:text-cream-50 transition-colors px-10 py-4 text-xs tracking-[0.28em] uppercase font-light"
                    >
                        Alle neuen Produkte ansehen
                        <ArrowRight
                            size={14}
                            className="transition-transform group-hover:translate-x-1"
                        />
                    </Link>
                </div>
            </div>
        </section>
    );
}
